/* eslint-disable @typescript-eslint/no-explicit-any */
import { WalletAddress } from "../Ether";
import { UserInviteCodes } from "./InviteCodes";
import { DepositState, UserAttributes, defaultUserAttributes } from "./UserAttributes";
import { defaultUserMetrics, UserMetrics } from "./UserMetrics";

export interface UserSchema {
  id: number;
  privy_id: string;
  privy_wallet_address: WalletAddress;
  short_wallet: string;
  username?: string;
  created_at: number;
  updated_at: number;
  twitter_username?: string;
  twitter_displayname?: string;
  twitter_picture?: string;
  twitter_auth_token?: string;
  twitter_refresh_token?: string;
  privy_twitter?: Record<string, any>;
  privy_discord?: Record<string, any>;
  privy_email?: Record<string, any>;
  privy_github?: Record<string, any>;
  privy_google?: Record<string, any>;
  privy_linked_accounts?: Record<string, any>;
  privy_phone?: Record<string, any>;
  privy_wallet?: Record<string, any>;
  privy_tiktok?: Record<string, any>;
  privy_linkedin?: Record<string, any>;
  privy_apple?: Record<string, any>;
  wallet_address_4337: WalletAddress;
  deposit_state?: string;
  free_gem_game_id?: number;
  installed_pwa?: boolean;
  free_gem_claimed?: boolean;
}

export interface PrivyUser {
  id: string;
  walletAddress: WalletAddress;
  twitter?: Record<string, any>;
  discord?: Record<string, any>;
  email?: Record<string, any>;
  github?: Record<string, any>;
  google?: Record<string, any>;
  linked_accounts?: Record<string, any>;
  phone?: Record<string, any>;
  wallet?: Record<string, any>;
  tiktok?: Record<string, any>;
  linkedin?: Record<string, any>;
  apple?: Record<string, any>;
}

export interface TwitterUser {
  handle?: string;
  displayName?: string;
  picture?: string;
  authToken?: string;
  refreshToken?: string;
}

export interface GemzUser {
  id: number;
  walletAddress: WalletAddress;
  shortWallet: string;
  username: string;
  created_at: number;
  updated_at: number;
  privy: PrivyUser;
  twitter: TwitterUser;
  depositState: DepositState;
  freeGemGameId?: number;
  freeGemClaimed: boolean;
  installedPWA: boolean;
  attributes: UserAttributes;
  metrics: UserMetrics;
}

export interface MyGemzUser extends GemzUser {
  inviteCodes: UserInviteCodes[];
  myConsumedInviteCode?: string;
}

const defaultUsername = 'Unknown';

export const getUserFromSchema = (schema: UserSchema, attributes?: UserAttributes, metrics?: UserMetrics): GemzUser => ({
  id: schema.id,
  walletAddress: schema.wallet_address_4337,
  shortWallet: schema.short_wallet,
  username: schema.username || defaultUsername,
  created_at: schema.created_at,
  updated_at: schema.updated_at,
  privy: {
    id: schema.privy_id,
    twitter: schema.privy_twitter,
    discord: schema.privy_discord,
    email: schema.privy_email,
    github: schema.privy_github,
    google: schema.privy_google,
    linked_accounts: schema.privy_linked_accounts,
    phone: schema.privy_phone,
    wallet: schema.privy_wallet,
    tiktok: schema.privy_tiktok,
    linkedin: schema.privy_linkedin,
    apple: schema.privy_apple,
    walletAddress: schema.privy_wallet_address,
  },
  twitter: {
    handle: schema.twitter_username,
    displayName: schema.twitter_displayname,
    picture: schema.twitter_picture,
    authToken: schema.twitter_auth_token,
    refreshToken: schema.twitter_refresh_token,
  },
  depositState: schema.deposit_state as DepositState,
  freeGemGameId: schema.free_gem_game_id,
  freeGemClaimed: Boolean(schema.free_gem_claimed),
  installedPWA: Boolean(schema.installed_pwa),
  attributes: attributes || defaultUserAttributes,
  metrics: metrics || defaultUserMetrics,
});

export const getSchemaFromUser = (user: GemzUser): UserSchema => ({
  id: user.id,
  privy_id: user.privy.id,
  privy_wallet_address: user.privy?.walletAddress,
  short_wallet: user.shortWallet,
  username: user.username === defaultUsername ? undefined : user.username,
  created_at: user.created_at,
  updated_at: user.updated_at,
  twitter_username: user.twitter.handle,
  twitter_displayname: user.twitter.displayName,
  twitter_picture: user.twitter.picture,
  twitter_auth_token: user.twitter.authToken,
  twitter_refresh_token: user.twitter.refreshToken,
  privy_twitter: user.privy.twitter,
  privy_discord: user.privy.discord,
  privy_email: user.privy.email,
  privy_github: user.privy.github,
  privy_google: user.privy.google,
  privy_linked_accounts: user.privy.linked_accounts,
  privy_phone: user.privy.phone,
  privy_wallet: user.privy.wallet,
  privy_tiktok: user.privy.tiktok,
  privy_linkedin: user.privy.linkedin,
  privy_apple: user.privy.apple,
  wallet_address_4337: user.walletAddress,
  deposit_state: user.depositState,
  installed_pwa: user.installedPWA ? true : undefined,
  free_gem_claimed: user.freeGemClaimed,
  free_gem_game_id: user.freeGemGameId && Number(user.freeGemGameId),
});
